import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Link } from "react-router-dom";
import { Calendar } from "../../components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { toast } from "sonner";
import { Plus, Repeat, Pencil, Trash2, Users2, User as UserIcon, PartyPopper, CircleDot, Clock4, CalendarCog, Link2, Check, Copy } from "lucide-react";
import RosterPicker from "../../components/RosterPicker";

const EVENT_TYPES = [
  { value: "private", label: "Private lesson", desc: "1-on-1 or small private group (siblings, friends)", icon: UserIcon, group: false },
  { value: "clinic", label: "Clinic", desc: "Structured small-group session (skills-focused)", icon: Users2, group: true },
  { value: "mixer", label: "Mixer / Social", desc: "Social play event — rotate partners, meet players", icon: PartyPopper, group: true },
  { value: "open_play", label: "Open play", desc: "Drop-in court time, loosely coached", icon: CircleDot, group: true },
  { value: "available_slot", label: "Available slot", desc: "Open 1-on-1 slot — first student to book gets it", icon: Clock4, group: false },
];

const WEEKDAYS = [
  { v: 0, s: "Mon" }, { v: 1, s: "Tue" }, { v: 2, s: "Wed" },
  { v: 3, s: "Thu" }, { v: 4, s: "Fri" }, { v: 5, s: "Sat" }, { v: 6, s: "Sun" },
];

function toLocalISOString(date, timeStr) {
  const [h, m] = (timeStr || "17:00").split(":").map(Number);
  const d = new Date(date);
  d.setHours(h, m, 0, 0);
  // Return a naive wall-clock ISO string (no timezone). The backend treats
  // this as the literal local time — so rules "09:00" and lessons "09:00" line up.
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:00`;
}

function toDateInput(date) {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function fromDateInput(val) {
  // Parse a YYYY-MM-DD string as local-date to avoid UTC drift
  if (!val) return new Date();
  const [y, m, d] = val.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

export default function CoachCalendar() {
  const [lessons, setLessons] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: "", description: "", date: new Date(), time: "17:00", duration_mins: 60,
    type: "private", student_id: "", location: "", price: 0, max_students: 1, courts: 1,
    recurrence: "", occurrences: 4,
    booked_students: [],
  });
  const [bulk, setBulk] = useState({
    weekdays: [1, 3], start_time: "17:00", duration_mins: 60, weeks: 4,
    type: "available_slot", title: "Open slot", location: "", price: 0, max_students: 1, courts: 1,
  });
  // Hide past events by default so the schedule stays focused on what's next.
  // Persist choice in localStorage so a coach who wants historical view keeps it.
  const [hidePast, setHidePast] = useState(() => {
    try { return localStorage.getItem("pbcoach.calendar.hidePast") !== "false"; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("pbcoach.calendar.hidePast", hidePast ? "true" : "false"); }
    catch { /* ignore */ }
  }, [hidePast]);

  const load = async () => {
    try {
      const [ls, st] = await Promise.all([api.get("/lessons"), api.get("/students")]);
      setLessons(ls.data || []);
      setStudents(st.data || []);
    } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  // Consider a lesson "past" if its end time is before now. Cancelled/
  // completed lessons are always dimmed even if in the future.
  const isPast = (l) => {
    if (!l?.scheduled_at) return false;
    const start = new Date(l.scheduled_at);
    const endMs = start.getTime() + (l.duration_mins || 0) * 60 * 1000;
    return endMs < Date.now();
  };

  const lessonsOnDay = useMemo(() => {
    const d = selectedDate.toDateString();
    return lessons
      .filter((l) => new Date(l.scheduled_at).toDateString() === d)
      .sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at));
  }, [lessons, selectedDate]);

  const { visibleOnDay, hiddenPastCount } = useMemo(() => {
    if (!hidePast) return { visibleOnDay: lessonsOnDay, hiddenPastCount: 0 };
    const past = lessonsOnDay.filter(isPast);
    return {
      visibleOnDay: lessonsOnDay.filter((l) => !isPast(l)),
      hiddenPastCount: past.length,
    };
  }, [lessonsOnDay, hidePast]);

  const daysWithLessons = useMemo(() => lessons.map((l) => new Date(l.scheduled_at)), [lessons]);

  const setFormField = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setBulkField = (k) => (e) => setBulk({ ...bulk, [k]: e.target.value });

  const currentType = EVENT_TYPES.find((t) => t.value === form.type) || EVENT_TYPES[0];
  const isGroupType = currentType.group;

  const create = async (e) => {
    e.preventDefault();
    try {
      const picked = (form.type === "private" || isGroupType) ? (form.booked_students || []) : [];
      // Private lesson: 0 picked = open slot, 1 = classic 1-on-1 (student_id),
      // 2+ = semi-private with pre-assigned roster (booked_students).
      let studentId = null;
      let preAssigned = [];
      if (form.type === "private") {
        if (picked.length === 1) studentId = picked[0];
        else if (picked.length >= 2) preAssigned = picked;
      } else if (isGroupType) {
        preAssigned = picked;
      }
      // Capacity: at minimum fits whoever is pre-assigned.
      const capacity = isGroupType
        ? Math.max(Number(form.max_students) || 4, preAssigned.length)
        : (preAssigned.length > 1 ? preAssigned.length : 1);
      const payload = {
        title: form.title,
        description: form.description,
        scheduled_at: toLocalISOString(form.date || selectedDate, form.time),
        duration_mins: Number(form.duration_mins) || 60,
        type: form.type,
        student_id: studentId,
        location: form.location,
        price: Number(form.price) || 0,
        max_students: capacity,
        courts: Number(form.courts) || 1,
        booked_students: preAssigned.length ? preAssigned : undefined,
        recurrence: form.recurrence || null,
        occurrences: form.recurrence ? (Number(form.occurrences) || 1) : 1,
      };
      const { data } = await api.post("/lessons", payload);
      toast.success(data?.series_count > 1 ? `Created ${data.series_count} recurring events` : "Event created");
      setOpen(false);
      setForm({ ...form, title: "", description: "", location: "", booked_students: [], student_id: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create event");
    }
  };

  // When the new-event dialog opens, seed its date from the currently-selected calendar day
  useEffect(() => {
    if (open) setForm((f) => ({ ...f, date: selectedDate }));
  }, [open, selectedDate]);

  const toggleWeekday = (v) => {
    const has = bulk.weekdays.includes(v);
    setBulk({ ...bulk, weekdays: has ? bulk.weekdays.filter((x) => x !== v) : [...bulk.weekdays, v].sort() });
  };

  const createBulk = async (e) => {
    e.preventDefault();
    if (bulk.weekdays.length === 0) { toast.error("Pick at least one weekday"); return; }
    try {
      const payload = {
        weekdays: bulk.weekdays,
        start_time: bulk.start_time,
        duration_mins: Number(bulk.duration_mins) || 60,
        weeks: Number(bulk.weeks) || 4,
        start_date: selectedDate.toISOString().slice(0, 10),
        type: bulk.type,
        title: bulk.title,
        location: bulk.location,
        price: Number(bulk.price) || 0,
        max_students: Number(bulk.max_students) || 1,
        courts: Number(bulk.courts) || 1,
      };
      const { data } = await api.post("/lessons/bulk-availability", payload);
      const skipped = data.skipped_conflicts || 0;
      if (data.count === 0 && skipped > 0) {
        toast.error(`All ${skipped} slot(s) conflict with existing lessons`);
      } else if (skipped > 0) {
        toast.success(`Published ${data.count} slots · skipped ${skipped} conflict(s)`);
      } else {
        toast.success(`Published ${data.count} slots`);
      }
      setBulkOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not publish");
    }
  };

  const openEdit = (lesson) => {
    const d = new Date(lesson.scheduled_at);
    setEditing({
      id: lesson.id,
      title: lesson.title,
      description: lesson.description || "",
      time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
      duration_mins: lesson.duration_mins,
      type: lesson.type,
      location: lesson.location || "",
      price: lesson.price || 0,
      max_students: lesson.max_students || 1,
      courts: lesson.courts || 1,
      date: d,
    });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    try {
      await api.patch(`/lessons/${editing.id}`, {
        title: editing.title,
        description: editing.description,
        scheduled_at: toLocalISOString(editing.date, editing.time),
        duration_mins: Number(editing.duration_mins) || 60,
        type: editing.type,
        location: editing.location,
        price: Number(editing.price) || 0,
        max_students: Number(editing.max_students) || 1,
        courts: Number(editing.courts) || 1,
      });
      toast.success("Event updated");
      setEditOpen(false); setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
  };

  const deleteLesson = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await api.delete(`/lessons/${id}`);
      toast.success("Deleted");
      load();
    } catch (_) { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="coach-calendar-page" className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Schedule</div>
          <h2 className="font-display text-4xl font-black uppercase tracking-tighter mt-2">Calendar</h2>
          <p className="text-sm text-[#A3A3A3] mt-2">Publish availability, run clinics, host mixers — your court, your rules.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/coach/availability" className="btn-secondary px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="availability-button">
            <CalendarCog size={14} /> Availability
          </Link>
          <IcalShareButton />
          <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
            <DialogTrigger asChild>
              <button className="btn-secondary px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="bulk-availability-button">
                <Repeat size={14} /> Publish availability
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-none bg-[#141414] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bulk-availability-dialog">
              <DialogHeader>
                <DialogTitle className="font-display uppercase tracking-tight text-2xl">Publish recurring availability</DialogTitle>
              </DialogHeader>
              <form onSubmit={createBulk} className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">Event type</label>
                  <TypeSelect value={bulk.type} onChange={(v) => setBulk({ ...bulk, type: v })} testId="bulk-type" hideIndividual />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold mb-2 block">Weekdays</label>
                  <div className="grid grid-cols-7 gap-2" data-testid="bulk-weekdays">
                    {WEEKDAYS.map((w) => {
                      const active = bulk.weekdays.includes(w.v);
                      return (
                        <button
                          type="button" key={w.v} onClick={() => toggleWeekday(w.v)}
                          className={`py-2 text-xs font-bold uppercase tracking-wider border ${active ? "bg-[#38BDF8] text-black border-[#38BDF8]" : "border-white/15 text-white hover:bg-white/5"}`}
                          data-testid={`bulk-weekday-${w.v}`}
                        >{w.s}</button>
                      );
                    })}
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <Field label="Start time" type="time" value={bulk.start_time} onChange={setBulkField("start_time")} testId="bulk-start-time" />
                  <Field label="Duration (min)" type="number" value={bulk.duration_mins} onChange={setBulkField("duration_mins")} testId="bulk-duration" />
                  <Field label="# weeks" type="number" min={1} max={26} value={bulk.weeks} onChange={setBulkField("weeks")} testId="bulk-weeks" />
                </div>
                <Field label="Title" value={bulk.title} onChange={setBulkField("title")} testId="bulk-title" />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <Field label="Location" value={bulk.location} onChange={setBulkField("location")} testId="bulk-location" />
                  <Field label="Price ($)" type="number" value={bulk.price} onChange={setBulkField("price")} testId="bulk-price" />
                  <Field label="Capacity" type="number" value={bulk.max_students} onChange={setBulkField("max_students")} testId="bulk-capacity" />
                </div>
                <Field label="# Courts" type="number" min={1} value={bulk.courts} onChange={setBulkField("courts")} testId="bulk-courts" />
                <div className="text-xs text-[#A3A3A3] border-l-2 border-[#38BDF8] pl-3 py-1">
                  Starts from the selected day on the left. Past dates in the current week are skipped.
                </div>
                <button className="btn-primary w-full py-3 text-sm" data-testid="bulk-submit">Publish slots</button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button className="btn-primary px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="new-lesson-button">
                <Plus size={14} /> New event
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-none bg-[#141414] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-lesson-dialog">
              <DialogHeader>
                <DialogTitle className="font-display uppercase tracking-tight text-2xl">New event on {(form.date || selectedDate).toDateString()}</DialogTitle>
              </DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <Field label="Title" value={form.title} onChange={setFormField("title")} required testId="new-lesson-title" />
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">Event type</label>
                  <TypeSelect value={form.type} onChange={(v) => setForm({ ...form, type: v })} testId="new-lesson-type" />
                </div>
                {(form.type === "private" || isGroupType) && (
                  <RosterPicker
                    students={students}
                    selectedIds={form.booked_students || []}
                    onChange={(ids) => setForm({ ...form, booked_students: ids, student_id: "" })}
                    mode={form.type === "private" ? "private" : "group"}
                  />
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Field label="Date" type="date" value={toDateInput(form.date || selectedDate)} onChange={(e) => setForm({ ...form, date: fromDateInput(e.target.value) })} testId="new-lesson-date" />
                  <Field label="Time" type="time" value={form.time} onChange={setFormField("time")} testId="new-lesson-time" />
                  <Field label="Duration (min)" type="number" value={form.duration_mins} onChange={setFormField("duration_mins")} testId="new-lesson-duration" />
                  <Field label="Price ($)" type="number" value={form.price} onChange={setFormField("price")} testId="new-lesson-price" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {isGroupType && (
                    <Field label="Capacity" type="number" value={form.max_students} onChange={setFormField("max_students")} testId="new-lesson-max" />
                  )}
                  <Field label="# Courts" type="number" min={1} value={form.courts} onChange={setFormField("courts")} testId="new-lesson-courts" />
                </div>
                <Field label="Location" value={form.location} onChange={setFormField("location")} testId="new-lesson-location" />

                {/* Recurrence */}
                <div className="border border-white/10 p-4 space-y-3" data-testid="recurrence-section">
                  <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-bold flex items-center gap-2">
                    <Repeat size={12} /> Recurrence (optional)
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">Repeats</label>
                      <select
                        value={form.recurrence}
                        onChange={setFormField("recurrence")}
                        className="w-full bg-transparent border-b border-white/20 py-2 outline-none text-sm"
                        data-testid="new-lesson-recurrence"
                      >
                        <option className="bg-[#141414]" value="">Does not repeat</option>
                        <option className="bg-[#141414]" value="weekly">Weekly</option>
                        <option className="bg-[#141414]" value="biweekly">Every 2 weeks</option>
                        <option className="bg-[#141414]" value="monthly">Monthly</option>
                      </select>
                    </div>
                    {form.recurrence && (
                      <Field
                        label="# Occurrences (incl. first)"
                        type="number" min={2} max={52}
                        value={form.occurrences} onChange={setFormField("occurrences")}
                        testId="new-lesson-occurrences"
                      />
                    )}
                  </div>
                </div>

                <button className="btn-primary w-full py-3 text-sm" data-testid="new-lesson-submit">
                  {form.recurrence && Number(form.occurrences) > 1
                    ? `Create ${form.occurrences} recurring events`
                    : "Create event"}
                </button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid lg:grid-cols-[auto_1fr] gap-6 md:gap-8">
        <div className="card-surface p-3 md:p-4 w-full lg:w-fit overflow-x-auto">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(d) => d && setSelectedDate(d)}
            modifiers={{ hasLesson: daysWithLessons }}
            modifiersClassNames={{ hasLesson: "bg-[#38BDF8]/20 text-[#38BDF8] font-bold" }}
            className="rounded-none"
          />
          <div className="mt-4 pt-4 border-t border-white/10 space-y-2 text-[11px] text-[#A3A3A3]">
            <div className="uppercase tracking-widest font-bold text-white/70 mb-2">Legend</div>
            {EVENT_TYPES.map((t) => {
              const I = t.icon;
              return (
                <div key={t.value} className="flex items-center gap-2">
                  <I size={12} className="text-[#38BDF8]" aria-hidden="true" />
                  <span>{t.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">
              {selectedDate.toDateString()}
            </div>
            <label className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#A3A3A3] cursor-pointer select-none" data-testid="hide-past-toggle">
              <input
                type="checkbox"
                checked={hidePast}
                onChange={(e) => setHidePast(e.target.checked)}
                className="accent-[#38BDF8] w-3.5 h-3.5"
                data-testid="hide-past-checkbox"
              />
              Hide past events
            </label>
          </div>
          {visibleOnDay.length === 0 ? (
            <div className="border border-dashed border-white/10 p-8 text-center text-[#A3A3A3]">
              {hiddenPastCount > 0 ? (
                <>
                  {hiddenPastCount} past event{hiddenPastCount > 1 ? "s" : ""} on this day are hidden.{" "}
                  <button className="underline hover:text-white" onClick={() => setHidePast(false)} data-testid="show-past-inline">
                    Show them
                  </button>.
                </>
              ) : (
                <>Nothing scheduled. Hit <strong className="text-white">New event</strong> or <strong className="text-white">Publish availability</strong> to add slots.</>
              )}
            </div>
          ) : (
            <>
              {visibleOnDay.map((l) => {
                const typeMeta = EVENT_TYPES.find((t) => t.value === l.type) || EVENT_TYPES[0];
                const I = typeMeta.icon;
                const past = isPast(l);
                const dim = past || l.status === "completed" || l.status === "cancelled";
                const statusPill = l.status === "completed"
                  ? { label: "Completed", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40" }
                  : l.status === "cancelled"
                    ? { label: "Cancelled", cls: "bg-red-500/15 text-red-300 border-red-400/40" }
                    : past
                      ? { label: "Past", cls: "bg-white/5 text-[#A3A3A3] border-white/15" }
                      : null;
              return (
                <div
                  key={l.id}
                  className={`card-surface p-5 flex items-center justify-between gap-4 transition-opacity ${dim ? "opacity-60" : ""}`}
                  data-testid={`day-lesson-${l.id}`}
                >
                  <div className="flex items-start gap-4 min-w-0 flex-1">
                    <div className="w-10 h-10 border border-[#38BDF8]/40 text-[#38BDF8] grid place-items-center shrink-0">
                      <I size={16} aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-bold flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span>
                          {new Date(l.scheduled_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · {l.duration_mins}min · {typeMeta.label}
                        </span>
                        {statusPill && (
                          <span
                            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest border ${statusPill.cls}`}
                            data-testid={`day-lesson-status-${l.id}`}
                          >
                            {statusPill.label}
                          </span>
                        )}
                      </div>
                      <div className={`font-display text-xl font-bold uppercase mt-1 truncate ${l.status === "completed" ? "line-through decoration-emerald-300/50" : l.status === "cancelled" ? "line-through decoration-red-300/60" : ""}`}>{l.title}</div>
                      <div className="text-xs text-[#A3A3A3] mt-1">
                        {typeMeta.group
                          ? `${(l.booked_students || []).length}/${l.max_students} booked`
                          : (l.type === "private" && (l.booked_students || []).length >= 2
                              ? `Semi-private · ${(l.booked_students || []).length} students`
                              : (l.student_id ? "Booked" : "Open slot"))}
                        {l.courts && l.courts > 0 ? ` · ${l.courts} court${l.courts > 1 ? "s" : ""}` : ""}
                        {l.location ? ` · ${l.location}` : ""}
                        {l.price ? ` · $${l.price}` : ""}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="w-9 h-9 border border-white/15 grid place-items-center hover:bg-white/5" aria-label="Quick edit" data-testid={`quick-edit-${l.id}`}>
                          <Pencil size={13} />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="end" className="w-44 rounded-none bg-[#141414] border-white/10 p-1">
                        <button className="w-full text-left px-3 py-2 text-xs hover:bg-white/5" onClick={() => openEdit(l)} data-testid={`popover-edit-${l.id}`}>
                          Edit details
                        </button>
                        <Link to={`/coach/lessons/${l.id}`} className="block w-full text-left px-3 py-2 text-xs hover:bg-white/5" data-testid={`popover-open-${l.id}`}>
                          Open full page
                        </Link>
                        <button className="w-full text-left px-3 py-2 text-xs text-red-400 hover:bg-red-500/10" onClick={() => deleteLesson(l.id)} data-testid={`popover-delete-${l.id}`}>
                          Delete event
                        </button>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              );
            })}
            {hidePast && hiddenPastCount > 0 && (
              <button
                onClick={() => setHidePast(false)}
                className="w-full border border-dashed border-white/10 py-3 text-[11px] uppercase tracking-widest text-[#A3A3A3] hover:text-white hover:border-white/25"
                data-testid="show-past-footer"
              >
                Show {hiddenPastCount} past event{hiddenPastCount > 1 ? "s" : ""} from this day
              </button>
            )}
            </>
          )}
        </div>
      </div>

      {/* Quick edit dialog */}
      <Dialog open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="rounded-none bg-[#141414] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-event-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">Edit event</DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={saveEdit} className="space-y-4">
              <Field label="Title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} required testId="edit-title" />
              <div>
                <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">Event type</label>
                <TypeSelect value={editing.type} onChange={(v) => setEditing({ ...editing, type: v })} testId="edit-type" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Date" type="date" value={toDateInput(editing.date)} onChange={(e) => setEditing({ ...editing, date: fromDateInput(e.target.value) })} testId="edit-date" />
                <Field label="Time" type="time" value={editing.time} onChange={(e) => setEditing({ ...editing, time: e.target.value })} testId="edit-time" />
                <Field label="Duration (min)" type="number" value={editing.duration_mins} onChange={(e) => setEditing({ ...editing, duration_mins: e.target.value })} testId="edit-duration" />
                <Field label="Price ($)" type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} testId="edit-price" />
              </div>
              {(EVENT_TYPES.find((t) => t.value === editing.type)?.group) && (
                <Field label="Capacity" type="number" value={editing.max_students} onChange={(e) => setEditing({ ...editing, max_students: e.target.value })} testId="edit-capacity" />
              )}
              <Field label="# Courts" type="number" min={1} value={editing.courts} onChange={(e) => setEditing({ ...editing, courts: e.target.value })} testId="edit-courts" />
              <Field label="Location" value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} testId="edit-location" />
              <button className="btn-primary w-full py-3 text-sm" data-testid="edit-submit">Save changes</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function IcalShareButton() {
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/ical/me");
      setInfo(data);
    } catch (_) {}
  };

  const rotate = async () => {
    if (!window.confirm("Rotate iCal token? Any existing subscriptions will stop working until you re-add the new URL.")) return;
    try {
      await api.post("/ical/rotate");
      load();
      toast.success("Token rotated");
    } catch (_) { toast.error("Could not rotate"); }
  };

  const url = info ? (info.url_absolute || (window.location.origin + info.url_path)) : "";

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch (_) {}
  };

  return (
    <Popover onOpenChange={(v) => v && !info && load()}>
      <PopoverTrigger asChild>
        <button className="btn-secondary px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="ical-share-button">
          <Link2 size={14} /> Share iCal
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 rounded-none bg-[#141414] border-white/10 p-4" data-testid="ical-popover">
        <div className="text-[10px] tracking-widest uppercase text-[#38BDF8] font-bold">Calendar feed</div>
        <div className="font-display text-lg font-black uppercase tracking-tight mt-1">Subscribe from any calendar</div>
        <p className="text-xs text-[#A3A3A3] mt-2">
          Copy this URL and add it as a subscription in Google Calendar, Apple Calendar, or Outlook. Updates every few hours automatically.
        </p>
        {url ? (
          <>
            <div className="mt-3 p-2 border border-white/10 bg-black/40 text-[11px] font-mono break-all" data-testid="ical-url">{url}</div>
            <div className="flex gap-2 mt-3">
              <button onClick={copy} className="btn-primary flex-1 py-2 text-xs inline-flex items-center justify-center gap-2" data-testid="ical-copy-button">
                {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy URL</>}
              </button>
              <button onClick={rotate} className="btn-secondary py-2 px-3 text-xs" data-testid="ical-rotate-button">Rotate</button>
            </div>
          </>
        ) : (
          <div className="mt-3 text-xs text-[#A3A3A3]">Loading…</div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function Field({ label, type = "text", value, onChange, required, testId, min, max, step }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">{label}</label>
      <input
        type={type} value={value} onChange={onChange} required={required}
        min={min} max={max} step={step}
        className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm"
        data-testid={testId}
      />
    </div>
  );
}

function TypeSelect({ value, onChange, testId, hideIndividual = false }) {
  const options = hideIndividual ? EVENT_TYPES.filter((t) => t.group || t.value === "available_slot") : EVENT_TYPES;
  const current = EVENT_TYPES.find((t) => t.value === value);
  return (
    <div className="space-y-2 mt-2">
      <div className="grid grid-cols-1 gap-1.5">
        {options.map((t) => {
          const I = t.icon;
          const active = value === t.value;
          return (
            <button
              type="button"
              key={t.value}
              onClick={() => onChange(t.value)}
              className={`text-left flex items-center gap-3 p-3 border transition-colors ${active ? "border-[#38BDF8] bg-[#38BDF8]/10" : "border-white/10 hover:bg-white/[0.03]"}`}
              data-testid={`${testId}-${t.value}`}
            >
              <I size={16} className={active ? "text-[#38BDF8]" : "text-white/70"} aria-hidden="true" />
              <div className="min-w-0">
                <div className={`text-sm font-bold uppercase tracking-wider ${active ? "text-[#38BDF8]" : "text-white"}`}>{t.label}</div>
                <div className="text-[11px] text-[#A3A3A3]">{t.desc}</div>
              </div>
            </button>
          );
        })}
      </div>
      {current && <div className="hidden">{current.value}</div>}
    </div>
  );
}
